
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { pdfjs, Document, Page } from "react-pdf";
import { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import { ExternalLinkIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import PDFViewerOverlay from "./PDFViewerOverlay";
import { Region } from "../interfaces";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface Props {
  pdfUrl: string
  regions?: Array<Region>;
  setCurrentPage?: (n: number) => void;
  setCurrentScale?: (n: number) => void;
  updateRegions?: ((regions: Array<Region>) => void);
  updatePageRects?: ((n: number, rect: DOMRect) => void);
  disabled?: boolean;
  /** Rendered page width in px. Defaults to 800. */
  pageWidth?: number;
  contentClassName?: string;
}

function PDFViewer(props: Props) {
  const { t } = useTranslation();
  const { setCurrentPage, setCurrentScale, updatePageRects } = props;

  const pageWidth = props.pageWidth ?? 800;
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const previewErrorState = (
    <div className="flex flex-col items-center justify-center h-full gap-3 p-6 text-center bg-gray-50">
      <div className="text-sm font-semibold text-gray-600">{t("This preview couldn't be loaded.")}</div>
      {props.pdfUrl &&
        <a href={props.pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-blue hover:underline">
          {t("Open file")}<ExternalLinkIcon className="h-4 w-4" />
        </a>
      }
    </div>
  );

  const pageNumbers: Array<number> = Array(pdfDocument?.numPages ?? 0).fill(1).map((one: number, i: number) => one + i);

  function handlePageLoad(page: PDFPageProxy) {
    if (page.pageNumber !== pageNumbers.length)
      return;

    setIsLoading(false);
    calculateVisibility();
  }

  const calculateVisibility = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer)
      return;

    let maxVisible = 0;
    let visiblePage = null;

    const pages = scrollContainer.querySelectorAll('.page');
    pages.forEach((page, index) => {
      const rect = page.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      const visibleHeight = Math.min(containerRect.bottom, rect.bottom) - Math.max(containerRect.top, rect.top);
      const visibleWidth = Math.min(containerRect.right, rect.right) - Math.max(containerRect.left, rect.left);

      const pageNumber = index + 1;

      if (visibleHeight > 0 && visibleWidth > 0) {
        const visibleArea = visibleHeight * visibleWidth;
        if (visibleArea > maxVisible) {
          maxVisible = visibleArea;
          visiblePage = pageNumber;
        }
      }

      updatePageRects?.(pageNumber, rect);
    });

    if (visiblePage) {
      if (setCurrentPage)
        setCurrentPage(visiblePage);

      if (setCurrentScale)
        pdfDocument?.getPage(visiblePage)
          .then(page => setCurrentScale(page.getViewport({ scale: 1 }).width / pageWidth));
    }

  }, [setCurrentPage, setCurrentScale, pdfDocument, updatePageRects, pageWidth]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container)
      return;

    container.addEventListener('scroll', calculateVisibility);
    return () => {
      container.removeEventListener('scroll', calculateVisibility);
    };

  }, [calculateVisibility]);

  return (
    <div className={`flex flex-col h-full overflow-y-auto ${isLoading && !hasError ? "bg-gray-100" : "bg-gray-50"} shadow-inner`} ref={scrollContainerRef}>
      <Document
        file={props.pdfUrl}
        options={{ isEvalSupported: false }}
        onLoadSuccess={(doc) => { setHasError(false); setPdfDocument(doc); }}
        onLoadError={() => { setIsLoading(false); setHasError(true); }}
        onSourceError={() => { setIsLoading(false); setHasError(true); }}
        className="flex-shrink-0 h-full"
        loading={<div className="bg-white rounded overflow-hidden shadow h-full" style={{ width: pageWidth }} />}
        error={previewErrorState}
      >
        <div className={`relative flex flex-col items-center ${props.contentClassName ?? "py-5 gap-3.5"}`} ref={pageContainerRef}>
          {pageNumbers.map((n: number) =>
            <div key={n} className="relative">
              <Page pageNumber={n} width={pageWidth} className={`rounded shadow overflow-hidden main-page-${n} page`} renderTextLayer={false} renderAnnotationLayer={false} loading={<div className="opacity-0" style={{ width: `${pageWidth}px` }} />} onLoadSuccess={handlePageLoad} />
              <PDFViewerOverlay pdfDocument={pdfDocument!} pageNumber={n} regions={props.regions} updateRegions={props.updateRegions} disabled={props.disabled} />
            </div>
          )}
        </div>
      </Document>
    </div>
  )

}

export default PDFViewer;
